import { isValueNullish } from "../../../utils/valueUtilities";

export const BOUNDARY_OPERATORS = {
	INCLUSIVE: "Inclusive",
	EXCLUSIVE: "Exclusive",
	INCLUSIVEEXCLUSIVE: "InclusiveExclusive",
	EXCLUSIVEINCLUSIVE: "ExclusiveInclusive",
	AXISNEARINCLUSIVE: "AxisNearInclusive",
	AXISFARINCLUSIVE: "AxisFarInclusive"
} as const;

export type BoundaryOperatorCode = keyof typeof BOUNDARY_OPERATORS;
export type BoundaryOperator = typeof BOUNDARY_OPERATORS[BoundaryOperatorCode];

const isBoundaryOperatorCode = (code: string): code is BoundaryOperatorCode =>
	Object.prototype.hasOwnProperty.call(BOUNDARY_OPERATORS, code);

export const getBoundaryOperator = (code?: string | null): BoundaryOperator => {
	if(isValueNullish(code)) {
		return BOUNDARY_OPERATORS.AXISNEARINCLUSIVE;
	}

	if(!isBoundaryOperatorCode(code as string)) {
		throw new Error(`Condition operator '${code}' is not supported by the Contains engine.`);
	}

	return BOUNDARY_OPERATORS[code];
};

export const includesBoundary = (
	boundaryOperator: BoundaryOperator,
	coordinate: number,
	lowerSideIsCovered: boolean,
	upperSideIsCovered: boolean
): boolean => {
	switch(boundaryOperator) {
		case BOUNDARY_OPERATORS.INCLUSIVE:
			return true;
		case BOUNDARY_OPERATORS.EXCLUSIVE:
			return false;
		case BOUNDARY_OPERATORS.INCLUSIVEEXCLUSIVE:
			return upperSideIsCovered;
		case BOUNDARY_OPERATORS.EXCLUSIVEINCLUSIVE:
			return lowerSideIsCovered;
		case BOUNDARY_OPERATORS.AXISNEARINCLUSIVE:
			return coordinate === 0
				|| (coordinate < 0 && lowerSideIsCovered)
				|| (coordinate > 0 && upperSideIsCovered);
		case BOUNDARY_OPERATORS.AXISFARINCLUSIVE:
			return coordinate !== 0
				&& ((coordinate < 0 && upperSideIsCovered)
					|| (coordinate > 0 && lowerSideIsCovered));
		default:
			throw new Error(`Unsupported Contains boundary operator '${boundaryOperator}'.`);
	}
};

export const includesEnvelopeBoundary = (
	boundaryOperator: BoundaryOperator,
	coordinate: number,
	oppositeCoordinate: number,
	isLowerBoundary: boolean
): boolean => {
	switch(boundaryOperator) {
		case BOUNDARY_OPERATORS.INCLUSIVE:
			return true;
		case BOUNDARY_OPERATORS.EXCLUSIVE:
			return false;
		case BOUNDARY_OPERATORS.INCLUSIVEEXCLUSIVE:
			return isLowerBoundary;
		case BOUNDARY_OPERATORS.EXCLUSIVEINCLUSIVE:
			return !isLowerBoundary;
		case BOUNDARY_OPERATORS.AXISNEARINCLUSIVE:
			return Math.abs(coordinate) < Math.abs(oppositeCoordinate);
		case BOUNDARY_OPERATORS.AXISFARINCLUSIVE:
			return Math.abs(coordinate) >= Math.abs(oppositeCoordinate);
		default:
			throw new Error(`Unsupported Contains boundary operator '${boundaryOperator}'.`);
	}
};

const stepDouble = (value: number, direction: number): number => {
	if(Number.isNaN(value)) {
		return value;
	}
	if(direction > 0 && value === Number.POSITIVE_INFINITY) {
		return value;
	}
	if(direction < 0 && value === Number.NEGATIVE_INFINITY) {
		return value;
	}
	if(value === 0) {
		return direction > 0 ? Number.MIN_VALUE : -Number.MIN_VALUE;
	}

	const bytes = new DataView(new ArrayBuffer(8));
	bytes.setFloat64(0, value);

	let highBits = bytes.getUint32(0);
	let lowBits = bytes.getUint32(4);
	const increment = value > 0 === direction > 0;
	if(increment) {
		lowBits = (lowBits + 1) >>> 0;
		if(lowBits === 0) {
			highBits = (highBits + 1) >>> 0;
		}
	}
	else if(lowBits === 0) {
		lowBits = 0xFFFFFFFF;
		highBits = (highBits - 1) >>> 0;
	}
	else {
		lowBits = (lowBits - 1) >>> 0;
	}

	bytes.setUint32(0, highBits);
	bytes.setUint32(4, lowBits);

	const result = bytes.getFloat64(0);
	return result;
};

const safeStep = (value: number, direction: number): number => stepDouble(value, direction);

export const nextUp = (value: number): number => {
	const result = safeStep(value, 1);
	return result;
};

export const nextDown = (value: number): number => {
	const result = safeStep(value, -1);
	return result;
};

export const allowsEndpoint = (
	boundaryOperator: BoundaryOperator,
	coordinate: number,
	localSideIsLower: boolean | undefined
) => {
	if(localSideIsLower === undefined) {
		return true;
	}

	switch(boundaryOperator) {
		case BOUNDARY_OPERATORS.INCLUSIVE:
			return true;
		case BOUNDARY_OPERATORS.EXCLUSIVE:
			return false;
		case BOUNDARY_OPERATORS.INCLUSIVEEXCLUSIVE:
			return !localSideIsLower;
		case BOUNDARY_OPERATORS.EXCLUSIVEINCLUSIVE:
			return localSideIsLower;
		case BOUNDARY_OPERATORS.AXISNEARINCLUSIVE:
			return coordinate === 0 || (coordinate < 0 ? localSideIsLower : !localSideIsLower);
		case BOUNDARY_OPERATORS.AXISFARINCLUSIVE:
			return coordinate !== 0 && (coordinate < 0 ? !localSideIsLower : localSideIsLower);
		default:
			return false;
	}
};